import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  LinearProgress,
  TextField,
} from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  LabelKeyWithValues,
  LabelValueWithKey,
} from '../../../interfaces/delivery-company/labels';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import axios from 'axios';
import { UserType } from '../../../enums/users';
import { displayMoney } from '../../../utils/money';
import { TurnedInNot } from '@mui/icons-material';

export interface LabelsInputProps {
  currentlySelectedLabels: LabelValueWithKey[];
  setCurrentlySelectedLabels: (labels: LabelValueWithKey[]) => void;
}

export const ExtractOptions = (labelKeys: LabelKeyWithValues[]) => {
  const options: LabelValueWithKey[] = [];
  labelKeys.forEach((labelKey) => {
    labelKey.values.forEach((labelValue) =>
      options.push({
        ...labelValue,
        key: labelKey.key,
      })
    );
  });
  return options;
};
function LabelsInput({
  currentlySelectedLabels,
  setCurrentlySelectedLabels,
}: LabelsInputProps) {
  const [labelInput, setLabelInput] = React.useState<string>('');
  const [availableLabels, setAvailableLabels] = React.useState<
    LabelValueWithKey[]
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [focusCount, setFocusCount] = useState<number>(0);

  const { user, config } = useContext(AuthContext);

  const isMerchant = user?.type === UserType.MERCHANT;

  useEffect(() => {
    const fetchAvailableLabels = async () => {
      if (focusCount != 1) return;
      setLoading(true);
      setErrorMessage('');
      const endpoint = `${API_ENDPOINT}/api/${isMerchant ? 'merchant' : 'org'}/labelkeys`;
      let pageNumber = 1;
      let hasNext = true;
      while (hasNext) {
        const currentEndpoint = `${endpoint}?page=${pageNumber}&page_size=1000`;
        try {
          const response = await axios.get<{
            count: number;
            next?: string;
            results: LabelKeyWithValues[];
          }>(currentEndpoint, config);
          console.log(response);
          setAvailableLabels([
            ...availableLabels,
            ...ExtractOptions(response.data.results),
          ]);
          if (!response.data.next) {
            hasNext = false;
          }
        } catch (e) {
          console.log(e);
          if (axios.isAxiosError(e)) {
            setErrorMessage('Failed to load labels: ' + e.message);
          } else {
            setErrorMessage('Failed to load labels');
          }
          setLoading(false);
          break;
        }

        pageNumber++;
      }

      setLoading(false);
    };
    fetchAvailableLabels();
  }, [focusCount]);

  const getNoOptionsText = () => {
    if (errorMessage) return 'No Options';
    if (loading) return 'Fetching labels...';
    if (!labelInput) return 'Start typing...';
    return 'No Options';
  };

  const getOptionLabel = (option: LabelValueWithKey): string => {
    let extraChargeRepr = displayMoney(option.extra_delivery_fee);
    const alreadySelected = currentlySelectedLabels.find(
      (item) => item.value === option.value
    );
    if (alreadySelected) {
      extraChargeRepr = displayMoney(alreadySelected.extra_delivery_fee);
    }

    const optionRepr = `${option.key}: ${option.value} ${extraChargeRepr != '0' ? `(+${extraChargeRepr})` : ''}`;
    return optionRepr;
  };

  return (
    <>
      <Autocomplete
        disablePortal
        multiple
        value={currentlySelectedLabels}
        onChange={(event: React.SyntheticEvent<Element, Event>, newValue) => {
          setCurrentlySelectedLabels(newValue);
          setLabelInput('');
        }}
        inputValue={labelInput}
        onInputChange={(event: any, newInputValue: string, reason) => {
          if (reason === 'input') setLabelInput(newInputValue);
        }}
        id="controllable-states-demo"
        options={availableLabels}
        noOptionsText={getNoOptionsText()}
        getOptionLabel={(option) => getOptionLabel(option)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(params, option) => {
          return (
            <li {...params} key={option.id}>
              <Chip icon={<TurnedInNot />} label={getOptionLabel(option)} />
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            autoComplete="off"
            {...params}
            label="Labels"
            onFocus={() => setFocusCount(focusCount + 1)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {loading && <CircularProgress />}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      {errorMessage && (
        <FormHelperText>
          <p style={{ color: 'red' }}>{errorMessage}</p>
        </FormHelperText>
      )}
    </>
  );
}

export default LabelsInput;
